import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FiEdit, FiSave, FiTrash2, FiPlus } from "react-icons/fi";
import { useToast } from "../../context/ToastContext";
import DeleteConfirmationModal from "../DeleteConfirmationModal/DeleteConfirmationModal";
import {
  addUpdateTimelineRemark,
  getTimelineRemarksByCourtRef,
  deleteTimelineRemark,
} from "../../services/api/TabViewerApi";
import "./style/RenderTimeline.scss";
import { useAuth } from "../../context/AuthContext";

gsap.registerPlugin(ScrollTrigger);

export const RenderTimeline = ({ timeline, courtref, isSupremeCourt }) => {
  const timelineRef = useRef(null);
  const { showToast } = useToast();
  const { role } = useAuth();

  // Remark states
  const [remarks, setRemarks] = useState({});
  const [editingRemark, setEditingRemark] = useState({});
  const [remarkInputs, setRemarkInputs] = useState({});
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    itemId: null,
    hearingDate: null,
  });

  function parseDMY(dateStr) {
    if (!dateStr || typeof dateStr !== "string") return null;
    const parts = dateStr.split("-");
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts;
    const date = new Date(`${yyyy}-${mm}-${dd}`);
    return isNaN(date.getTime()) ? null : date;
  }

  // Helper to check if date is in the future
  function isFutureDate(dateStr) {
    const date = parseDMY(dateStr);
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of today for accurate comparison
    return date > today;
  }

  // Fetch remarks on component mount
  useEffect(() => {
    if (!courtref) return;

    const fetchRemarks = async () => {
      try {
        const response = await getTimelineRemarksByCourtRef(courtref);

        // Handle the response structure - response.data should contain the array
        const remarksData = Array.isArray(response.data) ? response.data : [];
        // Convert array to object with hearingDate as key
        const remarksMap = {};
        remarksData.forEach((remark) => {
          remarksMap[remark.hearingDate] = remark;
        });

        setRemarks(remarksMap);

        // Initialize remark inputs with existing values
        const inputs = {};
        remarksData.forEach((remark) => {
          inputs[remark.hearingDate] = remark.remark || "";
        });
        setRemarkInputs(inputs);
      } catch (error) {
        console.error("Error fetching remarks:", error);
        console.error("Error details:", error.response?.data || error.message);
        setRemarks({});
      }
    };

    fetchRemarks();
  }, [courtref]);

  // Refresh remarks after CRUD operations
  const refreshRemarks = async () => {
    try {
      const response = await getTimelineRemarksByCourtRef(courtref);

      // Handle the response structure - response.data should contain the array
      const remarksData = Array.isArray(response.data) ? response.data : [];

      const remarksMap = {};
      const inputs = {};

      remarksData.forEach((remark) => {
        remarksMap[remark.hearingDate] = remark;
        inputs[remark.hearingDate] = remark.remark || "";
      });

      setRemarks(remarksMap);
      setRemarkInputs(inputs);
    } catch (error) {
      console.error("Error refreshing remarks:", error);
      console.error("Error details:", error.response?.data || error.message);
    }
  };

  // Handle save remark
  const handleSaveRemark = async (hearingDate) => {
    const remarkText = remarkInputs[hearingDate]?.trim();

    if (!remarkText) {
      showToast("Please enter a remark", "error");
      return;
    }

    if (remarkText.length > 100) {
      showToast("Remark cannot exceed 100 characters", "error");
      return;
    }

    try {
      const payload = {
        remark: remarkText,
        courtRef: courtref,
        hearingDate: hearingDate,
      };

      const isUpdate = remarks[hearingDate]; // Check before saving

      const response = await addUpdateTimelineRemark(payload);

      await refreshRemarks();

      setEditingRemark((prev) => ({ ...prev, [hearingDate]: false }));

      showToast(
        isUpdate
          ? "Remark updated successfully!"
          : "Remark added successfully!",
        "success"
      );
    } catch (error) {
      console.error("Error saving remark:", error);
      console.error("Error details:", error.response?.data || error.message);
      showToast("Failed to save remark. Please try again.", "error");
    }
  };

  // Handle delete remark
  const handleDeleteRemark = (hearingDate) => {
    const remark = remarks[hearingDate];
    if (remark) {
      setDeleteModal({
        isOpen: true,
        itemId: remark._id,
        hearingDate: hearingDate,
      });
    }
  };

  const confirmDeleteRemark = async () => {
    const { itemId, hearingDate } = deleteModal;
    setDeleteModal({ isOpen: false, itemId: null, hearingDate: null });

    try {
      const response = await deleteTimelineRemark(itemId);

      await refreshRemarks();

      setEditingRemark((prev) => ({ ...prev, [hearingDate]: false }));
      showToast("Remark deleted successfully!", "success");
    } catch (error) {
      console.error("Error deleting remark:", error);
      console.error("Error details:", error.response?.data || error.message);
      showToast("Failed to delete remark. Please try again.", "error");
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, itemId: null, hearingDate: null });
  };

  // Handle edit remark
  const handleEditRemark = (hearingDate) => {
    setEditingRemark((prev) => ({ ...prev, [hearingDate]: true }));
  };

  const handleCancelEdit = (hearingDate) => {
    setEditingRemark((prev) => ({ ...prev, [hearingDate]: false }));
    setRemarkInputs((prev) => ({
      ...prev,
      [hearingDate]: remarks[hearingDate]?.remark || "",
    }));
  };

  // Handle input change
  const handleRemarkInputChange = (hearingDate, value) => {
    setRemarkInputs((prev) => ({
      ...prev,
      [hearingDate]: value,
    }));
  };

  const statusDetails = timeline?.status;

  function parseDMY(dateStr) {
    if (!dateStr || typeof dateStr !== "string") return null;
    const parts = dateStr.split("-");
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts;
    const date = new Date(`${yyyy}-${mm}-${dd}`);
    return isNaN(date.getTime()) ? null : date;
  }

  const sortedTimelineData = timeline?.data
    ? [...timeline.data]
        .filter((item) => {
          if (!item.hearingDate || item.hearingDate === "No date available") {
            return true;
          }
          // Filter out future dates
          return !isFutureDate(item.hearingDate);
        })
        .sort((a, b) => {
          const dateA = parseDMY(a.hearingDate);
          const dateB = parseDMY(b.hearingDate);

          if (dateA && dateB) {
            // Both have valid dates, sort descending
            return dateB - dateA;
          } else if (dateA) {
            // a has date, b does not -> a comes first
            return -1;
          } else if (dateB) {
            // b has date, a does not -> b comes first
            return 1;
          } else {
            // Neither has a valid date, keep original order
            return 0;
          }
        })
    : [];

  return (
    <div
      className={`timeline-container ${
        timeline && timeline.data.length === 0 ? "no-timeline-data" : ""
      }`}
      ref={timelineRef}
    >
      {timeline && timeline.data.length === 0 ? (
        <div className="no-data-container">
          <h3 className="no-data-title">No Timeline Available</h3>
          <p className="no-data-message">
            No hearing dates or case timeline events have been recorded for this
            case yet.
          </p>
        </div>
      ) : (
        sortedTimelineData.map((info, index) => {
          const hearingDate = info.hearingDate || "No date available";
          const hasRemark = remarks[hearingDate];
          const isEditing = editingRemark[hearingDate];

          // Don't show remark functionality for "No date available" entries
          const shouldShowRemark =
            hearingDate !== "No date available" && info.hearingDate;

          return (
            <div className="timeline-item" key={index}>
              <div
                className={`timeline-dot ${
                  statusDetails?.["Case Status"] === "CASE DISPOSED"
                    ? "dot-disposed"
                    : ""
                }`}
              >
                <div className="tooltip">
                  <div className="tooltip-row">
                    <strong>Business Date - </strong>
                    <span>{info.businessOnDate || "N/A"}</span>
                  </div>
                  <div className="tooltip-row">
                    <strong>Cause List Type - </strong>
                    <span>{info.causeListType || "N/A"}</span>
                  </div>
                  <div className="tooltip-row">
                    <strong>Hearing Date - </strong>
                    <span>{info.hearingDate || "N/A"}</span>
                  </div>
                  {isSupremeCourt ? (
                    <>
                      <div className="tooltip-row">
                        <strong>Stage - </strong>
                        <span>{info.stage || "N/A"}</span>
                      </div>
                      <div className="tooltip-row">
                        <strong>Bench - </strong>
                        <span>{info.judge || "N/A"}</span>
                      </div>
                    </>
                  ) : (
                    <div className="tooltip-row">
                      <strong>Judge - </strong>
                      <span>{info.judge || "N/A"}</span>
                    </div>
                  )}
                  <div className="tooltip-row">
                    <strong>Purpose - </strong>
                    <span>{info.purposeOfHearing || "N/A"}</span>
                  </div>
                  {statusDetails && (
                    <>
                      <div className="tooltip-row">
                        <strong>Decision Date - </strong>
                        <span>{statusDetails["Decision Date"] || "N/A"}</span>
                      </div>
                      <div className="tooltip-row">
                        <strong>Case Status - </strong>
                        <span>{statusDetails["Case Status"] || "N/A"}</span>
                      </div>
                      <div className="tooltip-row">
                        <strong>Nature of Disposal - </strong>
                        <span>
                          {statusDetails["Nature of Disposal"] || "N/A"}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="timeline-content">
                <p>
                  <strong>Hearing Date:</strong> {hearingDate}
                </p>

                {/* Remark Section - Only show if hearing date is available */}
                {shouldShowRemark && role !== "customer" && (
                  <div className="timeline-remark">
                    <strong>Remark:</strong>
                    <div className="timeline-remark-container">
                      {!hasRemark && !isEditing ? (
                        <button
                          className="timeline-add-remark-btn"
                          onClick={() =>
                            setEditingRemark((prev) => ({
                              ...prev,
                              [hearingDate]: true,
                            }))
                          }
                        >
                          <FiPlus size={12} /> Add Remark
                        </button>
                      ) : isEditing ? (
                        <div className="timeline-remark-edit">
                          <textarea
                            value={remarkInputs[hearingDate] || ""}
                            onChange={(e) =>
                              handleRemarkInputChange(
                                hearingDate,
                                e.target.value
                              )
                            }
                            placeholder="Enter remark"
                            className="timeline-remark-input"
                            rows={3}
                            maxLength={100}
                          />
                          <div className="timeline-remark-actions">
                            <div className="timeline-remark-add-cross">
                              <button
                                onClick={() => handleSaveRemark(hearingDate)}
                                className="timeline-save-icon-btn"
                              >
                                <FiSave size={14} />
                              </button>
                              <button
                                onClick={() => handleCancelEdit(hearingDate)}
                                className="timeline-cancel-icon-btn"
                              >
                                ×
                              </button>
                            </div>
                            <div className="character-counter">
                              {(remarkInputs[hearingDate] || "").length}/100
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="timeline-remark-display">
                          <div className="timeline-remark-text">
                            {hasRemark.remark}
                          </div>
                          <div className="timeline-remark-actions-after">
                            <button
                              onClick={() => handleEditRemark(hearingDate)}
                              className="timeline-edit-icon-btn"
                            >
                              <FiEdit size={12} />
                            </button>
                            <button
                              onClick={() => handleDeleteRemark(hearingDate)}
                              className="timeline-delete-icon-btn"
                            >
                              <FiTrash2 size={12} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        itemType="remark"
        onConfirm={confirmDeleteRemark}
        onCancel={cancelDelete}
      />
    </div>
  );
};
